import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import type { Locale } from "../data/types";
import { ui } from "../data/i18n";

type Theme = "light" | "dark";

interface AppContextValue {
  locale: Locale;
  toggleLocale: () => void;
  theme: Theme;
  toggleTheme: () => void;
  t: (key: string) => string;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

const STORAGE_KEYS = { locale: "dc_locale", theme: "dc_theme" };

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "es";
  const saved = window.localStorage.getItem(STORAGE_KEYS.locale);
  if (saved === "es" || saved === "en") return saved;
  return navigator.language.toLowerCase().startsWith("en") ? "en" : "es";
}

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const saved = window.localStorage.getItem(STORAGE_KEYS.theme);
  if (saved === "light" || saved === "dark") return saved;
  return "dark";
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(getInitialLocale);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.setAttribute("lang", locale);
    window.localStorage.setItem(STORAGE_KEYS.theme, theme);
    window.localStorage.setItem(STORAGE_KEYS.locale, locale);
  }, [theme, locale]);

  const toggleLocale = useCallback(() => setLocale((l) => (l === "es" ? "en" : "es")), []);
  const toggleTheme = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);

  const t = useCallback((key: string) => ui[locale][key] ?? key, [locale]);

  return (
    <AppContext.Provider value={{ locale, toggleLocale, theme, toggleTheme, t }}>
      {children}
    </AppContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
